import React from 'react';
import { useTranslation } from 'react-i18next';

import { Arrow } from 'icons/untitledui';

import { PriorityCard } from 'priorities/components/PriorityCard/_index';
import * as Text from 'theme/components/Text.style';
import * as Styled from 'priorities/styles/InlinePriorities.style';

export interface InlinePriorityItem {
  id: string;
  prompt: string;
  variant: 'routine' | 'updateEvent';
  previousUpdate?: string;
  eventId?: string;
}

export interface InlinePrioritiesCarouselProps {
  items: InlinePriorityItem[];
  onViewAll: () => void;
  onRecord: (item: InlinePriorityItem) => void;
}

export default function InlinePrioritiesCarousel({
  items,
  onViewAll,
  onRecord,
}: InlinePrioritiesCarouselProps): JSX.Element | null {
  const { t } = useTranslation('pagePriorities');

  if (items.length === 0) {
    return null;
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onViewAll();
    }
  };

  return (
    <Styled.Container
      data-testid="inline-priorities-carousel"
      role="button"
      tabIndex={0}
      onClick={onViewAll}
      onKeyDown={handleKeyDown}
      aria-label={t('inlinePriorities.viewAllAriaLabel')}
    >
      <Styled.Header>
        <Text.P3Bold as="h2">
          {`${t('inlinePriorities.title')} (${items.length})`}
        </Text.P3Bold>
        <Styled.HeaderChevron aria-hidden>
          <Arrow.ChevronRight />
        </Styled.HeaderChevron>
      </Styled.Header>
      <Styled.CarouselRow>
        {items.map((item) => (
          <PriorityCard
            key={item.id}
            variant={item.variant}
            prompt={item.prompt}
            previousUpdate={item.previousUpdate}
            onRecord={() => onRecord(item)}
          />
        ))}
      </Styled.CarouselRow>
    </Styled.Container>
  );
}
